import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXXS, LabelS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import type { RGBColor } from '@sorare/core/src/style/types';

const Root = styled(Horizontal).attrs({ center: true })`
  min-width: calc(6 * var(--unit));
  min-height: var(--chip-small-height);
  display: flex;
  gap: 1px;
  padding: 0 var(--half-unit);
  border-radius: var(--chip-small-height);
  --color: rgba(var(--rgb-color), 0.1);
  border: 1px solid rgb(var(--rgb-color));
  color: rgb(var(--rgb-color));
  background:
    linear-gradient(var(--color), var(--color)),
    linear-gradient(-30deg, var(--c-nd-200) 50%, var(--c-nd-100) 50%);

  &.displayGreyChip {
    --color: var(--c-nl-300);
    border: 1px solid var(--c-nd-100);
    color: var(--c-nd-400);
  }
`;
const StyledHorizontal = styled(Horizontal)`
  gap: 2px;
`;

type Props = {
  bonusBasisPoints: number | null;
  hasUpcomingGame: boolean;
  points: number | null;
  rgbColor: RGBColor;
};

export const BonusOrPointsChip = ({
  bonusBasisPoints,
  hasUpcomingGame,
  points,
  rgbColor,
}: Props) => {
  const displayGreyChip =
    bonusBasisPoints === null && points === 0 && hasUpcomingGame;

  return (
    <Root
      style={
        {
          '--rgb-color': rgbColor,
        } as CSSProperties
      }
      className={classNames({ displayGreyChip })}
    >
      {typeof bonusBasisPoints === 'number' ? (
        <>
          <LabelS bold>
            <FormattedNumber
              value={bonusBasisPoints / 100}
              maximumFractionDigits={1}
              signDisplay="always"
            />
          </LabelS>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="7"
            height="7"
            viewBox="0 0 7 7"
            fill="none"
          >
            <path
              d="M3.04546 1.77273C3.04546 1.07143 2.47403 0.5 1.77273 0.5C1.07143 0.5 0.5 1.07143 0.5 1.77273C0.5 2.47403 1.07143 3.04546 1.77273 3.04546C2.47403 3.04546 3.04546 2.47403 3.04546 1.77273ZM0.88961 6.42208C0.98052 6.42208 1.08442 6.38312 1.16234 6.3052L6.29221 1.17532C6.42208 1.03247 6.42208 0.785714 6.27922 0.642857C6.13637 0.5 5.88961 0.5 5.74676 0.642857L0.616883 5.77273C0.474026 5.91559 0.474026 6.16234 0.616883 6.3052C0.694805 6.38312 0.785714 6.42208 0.88961 6.42208ZM3.95455 5.22727C3.95455 5.92857 4.52598 6.5 5.22727 6.5C5.92857 6.5 6.5 5.92857 6.5 5.22727C6.5 4.52598 5.92857 3.95455 5.22727 3.95455C4.52598 3.95455 3.95455 4.52598 3.95455 5.22727Z"
              fill="currentColor"
            />
          </svg>
        </>
      ) : (
        <LabelS bold>
          <StyledHorizontal gap={0}>
            <FormattedMessage
              {...fantasy.ordinalPoints}
              values={{
                score: displayGreyChip ? (
                  '-'
                ) : (
                  <FormattedNumber
                    value={points || 0}
                    maximumFractionDigits={1}
                    signDisplay="always"
                  />
                ),
                sup: (...chunks: ReactNode[]) => (
                  <BrandXXS bold>{chunks}</BrandXXS>
                ),
              }}
            />
          </StyledHorizontal>
        </LabelS>
      )}
    </Root>
  );
};
